import { Dropzone } from "@/components/shared/common/Dropzone";
import type { HiringMatrix, Position } from "@/types";

import type { ScreeningJob } from "./types";
import { StatusBadge } from "./StatusBadge";

interface ScreenPageProps {
  positions: Position[];
  matrices: HiringMatrix[];
  selectedPositionId: number | null;
  selectedMatrixId: number | null;
  selectedMatrix: HiringMatrix | null;
  jobs: ScreeningJob[];
  onPositionChange: (id: number | null) => void;
  onMatrixChange: (id: number | null) => void;
  onFiles: (files: File[]) => void;
  onClearJobs: () => void;
  noMatrices: boolean;
  onGoToMatrices: () => void;
  pendingFile: File | null;
  onClearPendingFile: () => void;
  usePipeline: boolean;
  onTogglePipeline: (v: boolean) => void;
  onForceScreen: (job: ScreeningJob) => void;
  onViewCandidate: (id: number) => void;
}

export function ScreenPage({
  positions,
  matrices,
  selectedPositionId,
  selectedMatrixId,
  selectedMatrix,
  jobs,
  onPositionChange,
  onMatrixChange,
  onFiles,
  onClearJobs,
  noMatrices,
  onGoToMatrices,
  pendingFile,
  onClearPendingFile,
  usePipeline,
  onTogglePipeline,
  onForceScreen,
  onViewCandidate,
}: ScreenPageProps) {
  const canScreenNow = selectedPositionId != null && selectedMatrixId != null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
          <div className="flex flex-col gap-6 pb-2">
            <div className="flex flex-col rounded-card bg-white p-6 shadow-card">
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                    Position
                  </label>
                  <select
                    className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                    value={selectedPositionId ?? ""}
                    onChange={(e) => onPositionChange(Number(e.target.value) || null)}
                  >
                    {positions.length === 0 && <option value="">— no positions —</option>}
                    {positions.map((p) => (
                      <option key={p.id} value={p.id}>{p.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                    Hiring matrix
                  </label>
                  <select
                    className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                    value={selectedMatrixId ?? ""}
                    onChange={(e) => onMatrixChange(Number(e.target.value) || null)}
                  >
                    {matrices.length === 0 && <option value="">— no matrices —</option>}
                    {matrices.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedMatrix && (
                <div className="mb-4 rounded-badge bg-surface-secondary p-3 text-xs text-text-secondary">
                  <div className="font-semibold uppercase tracking-[0.32px] text-text-focused">
                    {selectedMatrix.name}
                  </div>
                  <ul className="mt-1.5 space-y-0.5">
                    {selectedMatrix.criteria.map((c) => (
                      <li key={c.name}>
                        <span className="font-medium text-near-black">{c.name}</span>{" "}
                        <span className="text-text-secondary">(w {c.weight})</span> — {c.requirement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!canScreenNow && (
                <div className="mb-4 rounded-badge bg-rausch-light p-3 text-sm text-rausch">
                  {positions.length === 0
                    ? "No positions found. Create a position and a hiring matrix before screening resumes."
                    : noMatrices
                      ? <><span>No hiring matrices found. </span><button onClick={onGoToMatrices} className="font-semibold underline">Create one</button><span> to start screening.</span></>
                      : "Select a position and hiring matrix to start screening."}
                </div>
              )}

              <div className="mb-4 flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <div
                    className={`relative h-5 w-9 rounded-full transition-colors ${usePipeline ? "bg-rausch" : "bg-border-card"}`}
                    onClick={() => onTogglePipeline(!usePipeline)}
                  >
                    <div
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${usePipeline ? "translate-x-4" : "translate-x-0.5"}`}
                    />
                  </div>
                  <span className="font-medium text-near-black">Full Pipeline</span>
                </label>
                <span className="text-xs text-text-secondary">
                  {usePipeline
                    ? "Screening + Intake + Salary Benchmark + Lifecycle stages"
                    : "Quick screen only"}
                </span>
              </div>

              {pendingFile && (
                <div className={`mb-4 flex items-center justify-between gap-3 rounded-card border p-4 ${canScreenNow ? "border-rausch/30 bg-rausch-light" : "border-amber-200 bg-amber-50"}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <svg className={`h-5 w-5 shrink-0 ${canScreenNow ? "text-rausch" : "text-amber-600"}`} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-near-black truncate">{pendingFile.name}</p>
                      <p className="text-xs text-text-secondary">
                        {canScreenNow ? "Ready to screen — click Screen Now to continue" : "Select a position and matrix to continue screening"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canScreenNow && (
                      <button
                        onClick={() => { onClearPendingFile(); onFiles([pendingFile]); }}
                        className="rounded-btn bg-rausch px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#e0334f] transition-colors"
                      >
                        Screen Now
                      </button>
                    )}
                    <button
                      onClick={onClearPendingFile}
                      className="rounded-btn p-1.5 text-text-secondary hover:text-near-black transition-colors"
                      title="Dismiss"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <Dropzone disabled={!canScreenNow} className="flex-1" onFiles={onFiles} />
            </div>

            {jobs.length > 0 && (
              <div className="rounded-card bg-white p-5 shadow-card">
                <div className="flex items-center justify-between">
                  <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
                    Uploads
                  </div>
                  <button
                    onClick={onClearJobs}
                    className="text-xs font-medium text-text-secondary transition-colors hover:text-error-text"
                  >
                    Clear
                  </button>
                </div>
                <ul className="mt-2 space-y-2 text-sm">
                  {jobs.map((j) =>
                    j.status === "duplicate" ? (
                      <li key={j.id} className="rounded-badge border border-amber-200 bg-amber-50 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate font-medium text-near-black">{j.filename}</p>
                            <p className="mt-0.5 text-xs text-amber-800">
                              Already screened — <span className="font-semibold">{j.existingCandidate?.name ?? "this candidate"}</span> exists for this position.
                            </p>
                          </div>
                          <span className="shrink-0 rounded-badge bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">Duplicate</span>
                        </div>
                        <div className="mt-2 flex gap-2">
                          {j.existingCandidate && (
                            <button
                              onClick={() => onViewCandidate(j.existingCandidate!.id)}
                              className="rounded-btn border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-50"
                            >
                              View existing
                            </button>
                          )}
                          {j.pendingBody && (
                            <button
                              onClick={() => onForceScreen(j)}
                              className="rounded-btn bg-rausch px-3 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90"
                            >
                              Screen anyway
                            </button>
                          )}
                        </div>
                      </li>
                    ) : (
                      <li key={j.id} className="flex items-center justify-between">
                        <span className="truncate font-medium text-near-black">{j.filename}</span>
                        <StatusBadge job={j} />
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
